
import React, { useState } from 'react';
import { Calendar, CalendarDays, CalendarRange, Table2, ListChecks, Settings2, ChevronLeft, ChevronRight, Copy, LogOut, User, Menu, X, ChevronDown, Package, Users, Stethoscope, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ViewType } from './Scheduler';
import DateNavigator from './DateNavigator';
import ClinicSwitcher from './ClinicSwitcher';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useIsDoctor } from '@/hooks/useIsDoctor';
import { useDoctorOnlyMode } from '@/hooks/useDoctorOnlyMode';
import { useReleases } from '@/hooks/useReleases';
import { VersionBadge } from './VersionBadge';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

interface SchedulerHeaderProps {
  currentView: ViewType;
  onViewChange: (view: ViewType) => void;
  currentDate: Date;
  onNavigate: (direction: 'prev' | 'next') => void;
  onDateChange: (date: Date) => void;
  copiedItemsCount: number;
  isLoading?: boolean;
}

const SchedulerHeader = ({
  currentView,
  onViewChange,
  currentDate,
  onNavigate,
  onDateChange,
  copiedItemsCount,
  isLoading = false,
}: SchedulerHeaderProps) => {
  const { user, logout, canAccessAdminPanel, canAccessPatients, canAccessInventory } = useAuth();
  const { isDoctor } = useIsDoctor();
  const { doctorOnly } = useDoctorOnlyMode();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSchedulerOpen, setMobileSchedulerOpen] = useState(false);

  const releases = useReleases();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Define which roles can access Tasks view
  const canAccessTasks = () => {
    if (!user) return false;
    return ['admin', 'operations_manager', 'office_admin', 'office_user'].includes(user.role);
  };

  // Define which roles can access Admin view
  const canAccessAdmin = () => {
    return canAccessAdminPanel();
  };

  const schedulerViews = [
    { view: 'daily' as ViewType, icon: Calendar, label: 'Daily' },
    { view: 'weekly' as ViewType, icon: CalendarDays, label: 'Weekly' },
    { view: 'monthly' as ViewType, icon: CalendarRange, label: 'Monthly' },
    { view: 'report' as ViewType, icon: Table2, label: 'Report' },
  ];

  // The "My Patients" entry is a doctor-private route (not a Scheduler
  // ViewType — it has its own page at /my-patients). We tag it with a custom
  // path so the rest of the header logic stays unchanged. See backlog #23.
  const isOnMyPatients = location.pathname.startsWith('/my-patients');

  // "Daily List" follows the same pattern: its own page at /daily-list,
  // gated like Patients (it surfaces Plato patient data).
  const isOnDailyList = location.pathname.startsWith('/daily-list');

  type TopLevelView = {
    view: ViewType | 'my-patients' | 'daily-list';
    icon: typeof Users;
    label: string;
    canAccess: boolean;
    path: string;
    isActive: boolean;
  };

  // Doctor-only mode (roadmap #11): internal doctors see ONLY My Patients -
  // the rest of the app stays hidden for them. Daily List was included here
  // until 2026-08-27, when Samuel asked that doctors see the referral side
  // only; it is one word away (doctorOnly ||) if that is ever reversed.
  const topLevelViews: TopLevelView[] = ([
    { view: 'my-patients', icon: Stethoscope, label: 'My Patients', canAccess: isDoctor, path: '/my-patients', isActive: isOnMyPatients },
    { view: 'daily-list', icon: CalendarDays, label: 'Daily List', canAccess: !doctorOnly && canAccessPatients(), path: '/daily-list', isActive: isOnDailyList },
    { view: 'patients', icon: Users, label: 'Referrals', canAccess: !doctorOnly && canAccessPatients(), path: '/patients', isActive: currentView === 'patients' && !isOnMyPatients && !isOnDailyList },
    { view: 'inventory', icon: Package, label: 'Inventory', canAccess: !doctorOnly && canAccessInventory(), path: '/inventory', isActive: currentView === 'inventory' && !isOnMyPatients },
    { view: 'tasks', icon: ListChecks, label: 'Task', canAccess: !doctorOnly && canAccessTasks(), path: '/tasks', isActive: currentView === 'tasks' && !isOnMyPatients },
    { view: 'automation', icon: Bot, label: 'Automation', canAccess: !doctorOnly && canAccessAdmin(), path: '/automation', isActive: currentView === 'automation' && !isOnMyPatients },
    { view: 'admin', icon: Settings2, label: 'Admin', canAccess: !doctorOnly && canAccessAdmin(), path: '/admin', isActive: currentView === 'admin' && !isOnMyPatients },
  ] as TopLevelView[]).filter((button) => button.canAccess);

  const isSchedulerView = !doctorOnly && !isOnMyPatients && schedulerViews.some(({ view }) => view === currentView);

  const handleMobileViewChange = (view: ViewType) => {
    if (!isLoading) {
      onViewChange(view);
      setMobileMenuOpen(false);
      setMobileSchedulerOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      {/* Main Header Row */}
      <div className="px-3 py-2 md:px-6 md:py-3">
        {/* The full single-row header (logo + title + nav + switcher + user +
            date nav) needs ~1450px. Below 2xl the controls wrap to a second
            row instead of squeezing flex items below min-content, which makes
            them paint over each other. */}
        <div className="flex flex-wrap 2xl:flex-nowrap items-center justify-between gap-2 gap-y-1 min-w-0">
          {/* Left Section - Logo and Mobile Menu Toggle */}
          <div className="flex flex-wrap xl:flex-nowrap items-center gap-2 md:gap-4 xl:gap-8 min-w-0">
            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden h-8 w-8 shrink-0"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>

            {/* Logo */}
            <div className="flex items-center gap-2 md:gap-3 min-w-0">
              <img
                src="/twin-city-dental.svg"
                alt="Twin City Endodontics"
                className="h-7 sm:h-8 md:h-10 shrink-0"
              />
              {/* Title text needs ~220px; on iPad-width desktop layouts it collides
                  with the nav pills, so show it only from xl up (logo always shows).
                  shrink-0 so it is never squeezed into overlapping paint. */}
              <div className="hidden sm:block md:hidden xl:block shrink-0">
                <h1 className="text-lg md:text-xl font-semibold text-gray-900 truncate">Twin City Endodontics</h1>
                <p className="text-xs text-gray-500 hidden md:block">Endodontics Practice Management</p>
              </div>
            </div>

            {/* View Buttons - Desktop only. Wraps at iPad-portrait width so the
                clinic switcher never bleeds past the viewport edge. */}
            <nav className="hidden md:flex flex-wrap items-center gap-2 lg:gap-4 min-w-0">
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                {/* Doctor-only accounts never see the Scheduler. The gate was on
                    isSchedulerView (the ACTIVE styling) but never on the control
                    itself, so a doctor login still had the whole scheduler one
                    click away. Found by signing in as one, 2026-08-27. */}
                {!doctorOnly && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={isLoading}
                        className={cn(
                          'relative px-3 py-1.5 h-8 transition-all duration-200',
                          isSchedulerView
                            ? 'bg-white text-primary shadow-sm font-medium'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-transparent',
                          isLoading && 'opacity-50 cursor-not-allowed'
                        )}
                      >
                        <Calendar className="h-4 w-4 mr-1.5" />
                        <span className="text-sm">Scheduler</span>
                        <ChevronDown className="h-4 w-4 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-44">
                      {schedulerViews.map(({ view, icon: Icon, label }) => (
                        <DropdownMenuItem
                          key={view}
                          onClick={() => !isLoading && onViewChange(view)}
                          className={cn(
                            'gap-2',
                            currentView === view && 'bg-accent text-accent-foreground'
                          )}
                        >
                          <Icon className="h-4 w-4" />
                          <span>{label}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {topLevelViews.map(({ view, icon: Icon, label, path, isActive }) => (
                  <Button
                    key={view}
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'relative px-3 py-1.5 h-8 transition-all duration-200',
                      isActive
                        ? 'bg-white text-primary shadow-sm font-medium'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-transparent'
                    )}
                    asChild
                  >
                    <NavLink to={path}>
                      <Icon className="h-4 w-4 mr-1.5" />
                      <span className="text-sm">{label}</span>
                    </NavLink>
                  </Button>
                ))}
              </div>

              {/* Clinic Switcher - next to view buttons */}
              <div className="h-8 w-px bg-gray-200" />
              <ClinicSwitcher />
            </nav>
          </div>

          {/* Right Section - Controls. No min-w-0: its min-content width is what
              pushes it onto the wrapped second row when the first row is full. */}
          <div className="flex items-center justify-end gap-1 sm:gap-2 md:gap-4 flex-1">
            {/* User Info and Logout - Desktop */}
            <div className="hidden md:flex items-center gap-3">
              <VersionBadge releases={releases} />
              <div className="hidden xl:flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-gray-500" />
                <span className="text-gray-700">{user?.name || user?.email}</span>
              </div>
              <div className="hidden xl:block h-6 w-px bg-gray-200" />
              <Button
                size="sm"
                variant="ghost"
                onClick={handleLogout}
                className="text-gray-600 hover:text-gray-900"
              >
                <LogOut className="h-4 w-4 mr-1.5" />
                Logout
              </Button>
            </div>

            {/* Date Navigation. Doctors filter by period in the My Patients table instead. */}
            {!doctorOnly && (
              <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => onNavigate('prev')}
                  className="h-8 w-8 hover:bg-gray-100 shrink-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                <DateNavigator
                  currentDate={currentDate}
                  currentView={currentView}
                  onDateChange={onDateChange}
                  className="max-w-full"
                />

                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => onNavigate('next')}
                  className="h-8 w-8 hover:bg-gray-100 shrink-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Copied Items Badge */}
            {copiedItemsCount > 0 && (
              <Badge
                variant="secondary"
                className="bg-secondary text-secondary-foreground border-secondary px-2 py-1 text-xs hidden sm:flex"
              >
                <Copy className="h-3 w-3 mr-1" />
                {copiedItemsCount}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white px-3 py-3 space-y-3">
          {/* View Buttons */}
          <div className="space-y-2">
            {/* Doctor-only accounts do not get the Scheduler on mobile either. */}
            {!doctorOnly && (
              <>
                <Button
                  variant={isSchedulerView ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setMobileSchedulerOpen((open) => !open)}
                  disabled={isLoading}
                  className={cn(
                    'w-full h-10 text-xs justify-between gap-1.5',
                    isLoading && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <span className="flex items-center gap-1.5 min-w-0">
                    <Calendar className="h-4 w-4 shrink-0" />
                    <span className="truncate">Scheduler</span>
                  </span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 shrink-0 transition-transform duration-200',
                      mobileSchedulerOpen && 'rotate-180'
                    )}
                  />
                </Button>

                {mobileSchedulerOpen && (
                  <div className="grid grid-cols-2 gap-2 pl-2">
                    {schedulerViews.map(({ view, icon: Icon, label }) => (
                      <Button
                        key={view}
                        variant={currentView === view ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleMobileViewChange(view)}
                        disabled={isLoading}
                        className={cn(
                          'h-10 text-xs justify-start gap-1.5',
                          isLoading && 'opacity-50 cursor-not-allowed'
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{label}</span>
                      </Button>
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="grid grid-cols-2 gap-2">
              {topLevelViews.map(({ view, icon: Icon, label, path, isActive }) => (
                <Button
                  key={view}
                  variant={isActive ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    'h-10 text-xs justify-start gap-1.5'
                  )}
                  asChild
                >
                  <NavLink
                    to={path}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setMobileSchedulerOpen(false);
                    }}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{label}</span>
                  </NavLink>
                </Button>
              ))}
            </div>
          </div>

          {/* Clinic Switcher */}
          <div className="flex items-center gap-2">
            <ClinicSwitcher />
          </div>

          {/* Version badge - mobile menu */}
          <div className="flex items-center pt-2 border-t border-gray-100">
            <VersionBadge releases={releases} />
          </div>

          {/* User Info and Logout */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2 text-sm min-w-0">
              <User className="h-4 w-4 text-gray-500 shrink-0" />
              <span className="text-gray-700 truncate">{user?.name || user?.email}</span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleLogout}
              className="text-gray-600 hover:text-gray-900 shrink-0"
            >
              <LogOut className="h-4 w-4 mr-1" />
              Logout
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default SchedulerHeader;
